import React, { useState, useContext, useEffect } from "react";
import navIMG from "../../assets/app/Nav/LOGO-FONDO-NEGRO.png";
import navIMGLight from "../../assets/app/Nav/LOGO-FONDO-NEGRO-2.png";
import { useNavigate, useLocation, Link } from "react-router-dom"; // Importar Link
import { useTranslation } from "react-i18next";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faTimes,
  faSun,
  faMoon,
} from "@fortawesome/free-solid-svg-icons";
import changeLanguage from "../../services/ChangeLanguage";
import "../../styles/app/Nav.css";

import CustomDropdown from "./CustomDropdown";
import { ThemeContext } from "../../contexts/ThemeContext";

// Definimos los IDs de las secciones para evitar "magic strings"
const SECTION_IDS = {
  PRESENTATION: "presentation",
  NOSOTROS: "nosotros",
  SERVICIOS: "servicios",
  PLANES: "planes",
};

const Nav: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const themeContext = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  if (!themeContext) {
    // El error es útil, lo mantenemos.
    throw new Error("ThemeContext must be used within a ThemeProvider");
  }

  const { isLightMode, toggleTheme } = themeContext;

  // Este efecto AHORA solo maneja el atributo del body.
  // Ya no cierra el menú incorrectamente.
  useEffect(() => {
    document.body.setAttribute("data-theme", isLightMode ? "light" : "dark");
  }, [isLightMode]);

  // Cierra el menú si la ruta cambia (ej. al navegar a /Blogs)
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    const isHome = location.pathname === "/" || location.pathname === "/index";

    if (isHome) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        setMenuOpen(false); // Cerramos el menú después de hacer scroll
      }
    } else {
      // RECOMENDACIÓN: Es mejor usar state de react-router que sessionStorage
      // navigate("/", { state: { scrollTo: id } });
      sessionStorage.setItem("scrollTo", id);
      navigate("/");
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    scrollToSection(SECTION_IDS.PRESENTATION);
  };

  const handleNavClick = (id: string) => {
    scrollToSection(id);
    // setMenuOpen(false) ya lo hace scrollToSection si está en home
  };

  const languageOptions = [
    { value: "en", label: "English" },
    { value: "es", label: "Español" },
  ];

  const navLinksId = "main-nav-links"; // ID para accesibilidad

  return (
    <nav className="nav-container">
      <div className="nav-inner">
        {/* Logo */}
        <a href="/" onClick={handleLogoClick} className="nav-logo">
          <img
            src={isLightMode ? navIMGLight : navIMG}
            alt="Chatbot"
            className="nav-logo-img"
            loading="eager" // Correcto para el logo (LCP)
          />
        </a>

        {/* Botón hamburguesa */}
        <button
          className="menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={menuOpen} // MEJORA: A11y
          aria-controls={navLinksId} // MEJORA: A11y
        >
          <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} size="lg" />
        </button>

        {/* Enlaces del menú */}
        <div
          id={navLinksId} // MEJORA: A11y
          // --- FIX (2) ---
          // Eliminada la prop "key" que causaba el re-montado.
          className={`nav-links ${menuOpen ? "active" : ""}`}
        >
          <button
            className="nav-item-anchor"
            onClick={() => handleNavClick(SECTION_IDS.NOSOTROS)}
          >
            {t("nav.about")}
          </button>
          <button
            className="nav-item-anchor"
            onClick={() => handleNavClick(SECTION_IDS.SERVICIOS)}
          >
            {t("nav.services")}
          </button>
          <button
            className="nav-item-anchor"
            onClick={() => handleNavClick(SECTION_IDS.PLANES)}
          >
            {t("nav.plans")}
          </button>

          {/* MEJORA: Usar <Link> para navegación semántica */}
          <Link to="/Blogs" className="nav-item-anchor">
            {t("nav.blogs")}
          </Link>

          <CustomDropdown
            options={languageOptions}
            defaultValue={i18n.language || "es"}
            onChange={changeLanguage}
          />
          {/* BOTON MODO CLARO OCULTO TEMPORALMENTE 
          <div className="theme-switch-wrapper">
            <label className="theme-switch" htmlFor="checkbox">
              <input
                type="checkbox"
                id="checkbox"
                checked={isLightMode}
                onChange={toggleTheme}
              />
              <div className="theme-slider round">
                <FontAwesomeIcon
                  icon={isLightMode ? faSun : faMoon}
                  className="theme-icon"
                />
              </div>
            </label>
          </div>
          */}
        </div>
      </div>
    </nav>
  );
};

export default Nav;
